import { z } from 'zod';
// ============================================================
// 카테고리
// ============================================================
const HexColorSchema = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, '색상은 #RRGGBB 형식 hex 문자열이어야 합니다.');
export const WikiCategorySchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    sortOrder: z.number().int(),
    uploadOpenToUsers: z.boolean(),
    /** 지식 그래프에서 카테고리 노드 색상 (#RRGGBB). */
    color: z.string(),
    pageCount: z.number().int().nonnegative().optional(),
    createdAt: z.string(),
});
export const WikiCategoryListSchema = z.object({
    categories: z.array(WikiCategorySchema),
});
const CategorySlugSchema = z
    .string()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9-]+$/, '카테고리 slug 은 소문자 영문/숫자/하이픈만 허용합니다.');
export const CreateWikiCategoryRequestSchema = z.object({
    slug: CategorySlugSchema,
    name: z.string().min(1).max(60),
    description: z.string().max(500).nullable().default(null),
    uploadOpenToUsers: z.boolean().default(false),
    color: HexColorSchema.optional(),
});
export const UpdateWikiCategoryRequestSchema = z.object({
    name: z.string().min(1).max(60).optional(),
    description: z.string().max(500).nullable().optional(),
    uploadOpenToUsers: z.boolean().optional(),
    color: HexColorSchema.optional(),
});
export const ReorderWikiCategoriesRequestSchema = z.object({
    orderedIds: z.array(z.string().uuid()).min(1),
});
// ============================================================
// 페이지
// ============================================================
export const WikiPageSourceSchema = z.enum(['llm', 'manual', 'system']);
export const WikiPageSummarySchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    categoryId: z.string().uuid().nullable(),
    categorySlug: z.string().nullable(),
    takeaway: z.string(),
    updatedAt: z.string(),
});
export const WikiPageSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    categoryId: z.string().uuid().nullable(),
    categorySlug: z.string().nullable(),
    takeaway: z.string(),
    body: z.string(),
    source: WikiPageSourceSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    /**
     * 외부 sync (KMS 등) 으로 들어온 자료에서 만들어진 페이지면 원본 페이지 URL.
     * `wiki_document_pages` 로 연결된 가장 최근 `wiki_documents.external_url` (활성 문서만).
     * 없으면 null — innermate 내부에서 만들어진 페이지.
     */
    externalUrl: z.string().nullable(),
    /**
     * 이 페이지의 출처가 된 모든 활성(active) 문서 목록 (deleted/archived 제외).
     * 한 페이지가 여러 문서를 합쳐 만들어진 경우 다수, 내부 페이지면 빈 배열.
     */
    sourceDocuments: z.array(z.object({
        id: z.string().uuid(),
        originalName: z.string(),
        externalUrl: z.string().nullable(),
    })),
    /** 이 페이지를 가리키는 다른 페이지들 (백링크). 서버가 항상 배열로 응답. */
    backlinks: z.array(z.object({ slug: z.string(), title: z.string() })),
    /** 이 페이지에서 다른 페이지로 향하는 링크 (끊긴 링크 포함). */
    outlinks: z.array(z.object({
        slug: z.string(),
        resolved: z.boolean(),
    })),
});
export const WikiPageListSchema = z.object({
    pages: z.array(WikiPageSummarySchema),
    nextCursor: z.string().nullable(),
});
export const UpdateWikiPageRequestSchema = z.object({
    title: z.string().min(1).max(120),
    takeaway: z.string().min(1).max(1000),
    body: z.string().max(50000),
    categorySlug: CategorySlugSchema.nullable(),
});
// ============================================================
// 인덱스 (목차)
// ============================================================
export const WikiIndexEntrySchema = z.object({
    slug: z.string(),
    title: z.string(),
    takeaway: z.string(),
    updatedAt: z.string(),
});
export const WikiIndexCategorySchema = z.object({
    category: WikiCategorySchema,
    pages: z.array(WikiIndexEntrySchema),
});
export const WikiIndexSchema = z.object({
    categories: z.array(WikiIndexCategorySchema),
    uncategorized: z.array(WikiIndexEntrySchema),
    totalPages: z.number().int().nonnegative(),
});
// ============================================================
// 지식 그래프 (사용자 영역, sigma.js 시각화용)
// ============================================================
export const WikiGraphNodeSchema = z.object({
    id: z.string().uuid(),
    slug: z.string(),
    title: z.string(),
    categorySlug: z.string().nullable(),
    /** 카테고리가 있으면 그 색상, 없으면 null (uncategorized — 회색 처리). */
    categoryColor: z.string().nullable(),
    /** 페르소나 center 페이지면 true. 클라이언트가 강조 표시. */
    isCenter: z.boolean(),
    /** 카테고리 허브 페이지면 true. 클라가 별도 강조 (size 가중 등) 가능. */
    isCategoryHub: z.boolean(),
    /** 이 페이지를 가리키는 다른 페이지 수 (degree centrality 가중치 후보). */
    degree: z.number().int().nonnegative(),
});
export const WikiGraphEdgeSchema = z.object({
    fromSlug: z.string(),
    toSlug: z.string(),
    /** false 면 끊긴 링크 (대상 페이지 미존재) — 클라가 점선 처리. */
    resolved: z.boolean(),
});
// ============================================================
// 작업 이력 (사용자 영역, /wikis/log)
// ============================================================
export const WikiLogEntrySchema = z.object({
    revisionId: z.string().uuid(),
    pageId: z.string().uuid(),
    pageSlug: z.string(),
    pageTitle: z.string(),
    editedVia: z.enum(['llm-ingest', 'llm-health', 'manual']),
    /** 사람이면 표시명, 시스템(LLM 자동) 이면 null. */
    editorName: z.string().nullable(),
    note: z.string().nullable(),
    takeawayPreview: z.string(),
    createdAt: z.string(),
    /** 페이지가 soft-deleted 된 상태에서의 revision 인지. UI 가 회색/뱃지 처리. */
    pageRemoved: z.boolean(),
});
export const WikiLogResponseSchema = z.object({
    entries: z.array(WikiLogEntrySchema),
    /** 다음 페이지 cursor — 마지막 entry 의 createdAt+revisionId. null 이면 끝. */
    nextCursor: z.string().nullable(),
});
export const WikiGraphResponseSchema = z.object({
    nodes: z.array(WikiGraphNodeSchema),
    edges: z.array(WikiGraphEdgeSchema),
    /** 페르소나 center 슬러그. */
    centerSlug: z.string(),
    /** 카테고리 메타 (id, name, slug, color) — 클라가 범례/필터에 사용. */
    categories: z.array(z.object({
        slug: z.string(),
        name: z.string(),
        color: z.string(),
    })),
    generatedAt: z.string(),
});
// ============================================================
// 그래프 통계 (admin)
// ============================================================
export const WikiGraphStatsSchema = z.object({
    totalPages: z.number().int().nonnegative(),
    llmPages: z.number().int().nonnegative(),
    systemPages: z.number().int().nonnegative(),
    totalLinks: z.number().int().nonnegative(),
    brokenLinks: z.number().int().nonnegative(),
    pagesWithNoOutlinks: z.number().int().nonnegative(),
    pagesWithNoBacklinks: z.number().int().nonnegative(),
    isolatedPages: z.number().int().nonnegative(),
    leafOnlyPages: z.number().int().nonnegative(),
    avgOutlinks: z.number(),
    avgBacklinks: z.number(),
    categories: z.array(z.object({
        slug: z.string(),
        name: z.string(),
        pageCount: z.number().int().nonnegative(),
        internalLinks: z.number().int().nonnegative(),
        density: z.number(),
    })),
    embeddings: z.object({
        total: z.number().int().nonnegative(),
        embedded: z.number().int().nonnegative(),
        pending: z.number().int(),
    }),
});
export const WikiReembedAllResponseSchema = z.object({
    queued: z.number().int().nonnegative(),
});
export const WikiSimilarPagesResponseSchema = z.object({
    enabled: z.boolean(),
    reason: z.string().optional(),
    results: z.array(z.object({
        slug: z.string(),
        title: z.string(),
        takeaway: z.string(),
        distance: z.number(),
    })),
});
export const WikiHubsRegenerateResponseSchema = z.object({
    regenerated: z.number().int().nonnegative(),
    categories: z.array(z.string()),
});
// ============================================================
// 일괄 재ingest (Track A 분석을 기존 자료에 일괄 재적용)
// ============================================================
export const WikiReingestStartResponseSchema = z.object({
    /** 새로 enqueue 된 doc 수. 진행 중 batch 가 있어 미처리만 enqueue 한 경우 그 수. */
    queued: z.number().int().nonnegative(),
    /** batch 식별자. 진행 추적에 사용. */
    batchId: z.string().uuid(),
    /** 진행 중 batch 가 감지되어 미처리만 이어서 시작했으면 true. */
    resumed: z.boolean(),
    /** 사람에게 보일 메시지. */
    message: z.string(),
});
export const WikiReingestStatusSchema = z.object({
    batchId: z.string().uuid().nullable(),
    status: z.enum(['idle', 'running', 'done']),
    total: z.number().int().nonnegative(),
    processed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    startedAt: z.string().nullable(),
    finishedAt: z.string().nullable(),
    /** 가장 최근 처리한 doc 의 메타 (정지 의심 판단용). */
    lastStep: z
        .object({
        docId: z.string(),
        originalName: z.string().nullable(),
        status: z.enum(['done', 'failed']),
        error: z.string().nullable(),
        at: z.string(),
    })
        .nullable(),
});
/** 큐레이션은 LLM 호출이 길어 graphile-worker 큐 처리 — 트리거 즉시 응답. */
export const WikiHubCurateQueuedResponseSchema = z.object({
    queued: z.boolean(),
    message: z.string(),
});
// ============================================================
// 검색
// ============================================================
export const SearchWikiRequestSchema = z.object({
    q: z.string().min(1).max(200),
    categorySlug: z.string().optional(),
    limit: z.number().int().min(1).max(50).default(20),
});
export const SearchWikiResponseSchema = z.object({
    results: z.array(WikiPageSummarySchema.extend({
        snippet: z.string(),
        /**
         * 외부 sync (KMS 등) 출처의 원본 URL. 있으면 검색 결과 클릭 시 innermate 내부 페이지가
         * 아니라 이 URL 로 이동. 없으면 null — innermate 자체 페이지로 이동.
         */
        externalUrl: z.string().nullable(),
        /** 관련도 점수 (FTS ts_rank / RRF / 리랭크). 상위 노출·우세 판단 신호. */
        score: z.number().optional(),
        /** 결과 내 0-based 순위. */
        rank: z.number().int().optional(),
    })),
});
// ============================================================
// 문서 (원본 자료)
// ============================================================
export const WikiDocumentStatusSchema = z.enum([
    'uploaded',
    'parsing',
    'parsed',
    'digesting',
    'done',
    'failed',
    'deleted',
    'archived',
]);
/** 지원하는 파일 확장자. */
export const WikiDocumentExtSchema = z.enum(['pdf', 'hwp', 'docx', 'pptx', 'xlsx', 'md', 'txt']);
export const WikiDigestSummarySchema = z.object({
    pages: z.array(z.object({ slug: z.string(), action: z.enum(['created', 'updated']) })),
    totalChunks: z.number().int().optional(),
    processedChunks: z.number().int().optional(),
    currentChunkHeading: z.string().nullable().optional(),
    currentChunkPageRange: z.string().nullable().optional(),
    truncatedAtChunkCap: z.boolean().optional(),
    allChunks: z.number().int().optional(),
    imageUploadTotal: z.number().int().optional(),
    imageUploadFailed: z.number().int().optional(),
});
export const WikiDocumentSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    uploaderName: z.string().nullable(),
    categoryId: z.string().uuid().nullable(),
    categorySlug: z.string().nullable(),
    originalName: z.string(),
    ext: z.string(),
    mime: z.string(),
    sizeBytes: z.number().int().nonnegative(),
    status: WikiDocumentStatusSchema,
    parsedChars: z.number().int().nullable(),
    digestSummary: WikiDigestSummarySchema.nullable(),
    /** 외부 sync (KMS 등) 으로 들어온 자료면 원본 페이지 URL. UI 에서 매핑된 페이지 클릭 시 이 URL 로 redirect. */
    externalUrl: z.string().nullable(),
    /** KMS sync 시 마지막 수정자 표시명. null = 미수신 또는 innermate 직접 업로드. */
    lastModifiedByName: z.string().nullable(),
    error: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    parsedAt: z.string().nullable(),
    digestedAt: z.string().nullable(),
});
export const WikiDocumentListSchema = z.object({
    documents: z.array(WikiDocumentSchema),
    storageReady: z.boolean(),
    /** 다음 페이지 cursor (updatedAt ISO). null 이면 마지막. */
    nextCursor: z.string().nullable().optional(),
    /** 전체 자료 갯수 (필터 적용 후). UI 의 카운트 표시용. */
    total: z.number().int().optional(),
});
/** detail 응답 — list 의 모든 필드 + parsedMarkdown 본문. */
export const WikiDocumentDetailSchema = WikiDocumentSchema.extend({
    parsedMarkdown: z.string().nullable(),
});
/** 업로드 허용 최대 바이트 — 위키 자료 전용 (파일 첨부 500MB 와 별개로 100MB). */
export const WIKI_DOCUMENT_MAX_BYTES = 100 * 1024 * 1024;
// ============================================================
// 설정 (admin)
// ============================================================
export const WikiConfigSchema = z.object({
    schemaPrompt: z.string(),
    modeIngestPrompt: z.string(),
    modeHealthPrompt: z.string(),
    chatInjectPrompt: z.string(),
    ingestConcurrency: z.number().int().min(1).max(20),
    ingestLlmEndpointId: z.string().uuid().nullable(),
    healthLlmEndpointId: z.string().uuid().nullable(),
    visionLlmEndpointId: z.string().uuid().nullable(),
    embeddingLlmEndpointId: z.string().uuid().nullable(),
    rerankerLlmEndpointId: z.string().uuid().nullable(),
    rerankerKind: z.enum(['llm', 'cross_encoder']),
    rerankerTopK: z.number().int().min(1).max(50),
    ingestMarkdownCharCap: z.number().int().min(1000).max(500000),
    ingestToolIterationLimit: z.number().int().min(3).max(50),
    ingestMaxChunks: z.number().int().min(1).max(500),
    ingestChunkMaxChars: z.number().int().min(1000).max(20000),
    ingestChunkOverlap: z.number().int().min(0).max(2000),
    updatedAt: z.string(),
});
export const UpdateWikiConfigRequestSchema = z.object({
    schemaPrompt: z.string().max(20000).optional(),
    modeIngestPrompt: z.string().max(10000).optional(),
    modeHealthPrompt: z.string().max(10000).optional(),
    chatInjectPrompt: z.string().max(10000).optional(),
    ingestConcurrency: z.number().int().min(1).max(20).optional(),
    ingestLlmEndpointId: z.string().uuid().nullable().optional(),
    healthLlmEndpointId: z.string().uuid().nullable().optional(),
    visionLlmEndpointId: z.string().uuid().nullable().optional(),
    embeddingLlmEndpointId: z.string().uuid().nullable().optional(),
    rerankerLlmEndpointId: z.string().uuid().nullable().optional(),
    rerankerKind: z.enum(['llm', 'cross_encoder']).optional(),
    rerankerTopK: z.number().int().min(1).max(50).optional(),
    ingestMarkdownCharCap: z.number().int().min(1000).max(500000).optional(),
    ingestToolIterationLimit: z.number().int().min(3).max(50).optional(),
    ingestMaxChunks: z.number().int().min(1).max(500).optional(),
    ingestChunkMaxChars: z.number().int().min(1000).max(20000).optional(),
    ingestChunkOverlap: z.number().int().min(0).max(2000).optional(),
    /** true 면 새 embeddingLlmEndpointId 와 다른 endpoint 로 만든 wiki_page_embeddings 를 삭제 + 재 backfill 큐잉. */
    invalidateMismatchedEmbeddings: z.boolean().optional(),
});
export const WikiEmbeddingsByEndpointSchema = z.object({
    rows: z.array(z.object({
        endpointId: z.string().uuid().nullable(),
        endpointName: z.string().nullable(),
        endpointModel: z.string().nullable(),
        embeddingDim: z.number().int().nullable(),
        count: z.number().int(),
    })),
});
// ============================================================
// 런타임 상태
// ============================================================
export const WikiRuntimeKindSchema = z.enum([
    'java',
    'python',
    'pdfJar',
    'pdfHybridSidecar',
    'pdfImages',
    'pyhwp',
    'libreoffice',
    'manifest',
    /** 태블로 통합 — venv 안의 tableauhyperapi 패키지. */
    'tableauHyperApi',
    /** 태블로 통합 — venv 안의 duckdb 패키지 (hyper → duckdb 변환용). */
    'duckdbPython',
]);
export const WikiRuntimeStatusSchema = z.object({
    kind: WikiRuntimeKindSchema,
    ok: z.boolean(),
    version: z.string().nullable(),
    path: z.string().nullable(),
    error: z.string().nullable(),
    /** 어떤 위키 자료 변환에 필요한지 요약. */
    requiredFor: z.array(z.string()),
});
export const WikiRuntimeReportSchema = z.object({
    runtimes: z.array(WikiRuntimeStatusSchema),
    manifestPath: z.string().nullable(),
    checkedAt: z.string(),
    bootstrapInstructions: z.string(),
});
// ============================================================
// 건강검진
// ============================================================
export const WikiHealthIssueSchema = z.object({
    kind: z.enum(['broken_link', 'orphan_page', 'duplicate', 'inconsistency', 'missing_takeaway']),
    pageSlug: z.string().nullable(),
    message: z.string(),
});
export const WikiHealthCheckReportSchema = z.object({
    runId: z.string().uuid(),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    status: z.enum(['running', 'done', 'failed']),
    pageCount: z.number().int().nonnegative(),
    issues: z.array(WikiHealthIssueSchema),
    llmReport: z.string(),
    error: z.string().nullable(),
});
// ============================================================
// 업로드 응답
// ============================================================
export const WikiUploadResponseSchema = z.object({
    document: WikiDocumentSchema,
});
// ============================================================
// 업로드 요청 폼 필드 검증 (multipart 와 함께 옴)
// ============================================================
export const WikiUploadFormFieldsSchema = z.object({
    categorySlug: CategorySlugSchema.nullable().default(null),
});
// ============================================================
// 텍스트 직접 입력 — 사내 게시판 복사붙여넣기 등
// ============================================================
/** 'auto-cleanup' = LLM 이 raw 텍스트를 markdown 으로 정리. 'as-is' = 그대로 markdown 으로 사용. */
export const WikiTextCleanupModeSchema = z.enum(['auto-cleanup', 'as-is']);
/** 입력 텍스트 안전 cap (markdown 정리 후에도 1MB 안전 한도 안에 들어와야 함). */
export const WIKI_TEXT_INPUT_MAX_CHARS = 200_000;
export const CreateWikiTextDocumentRequestSchema = z.object({
    /** 문서 제목 — wiki_documents.original_name 으로 저장. */
    title: z.string().min(1).max(200),
    categorySlug: CategorySlugSchema.nullable().default(null),
    /** 사용자가 textarea 에 붙여넣은 raw 텍스트. 단순 plain text 또는 일부 markdown 가능. */
    rawText: z.string().min(1).max(WIKI_TEXT_INPUT_MAX_CHARS),
    mode: WikiTextCleanupModeSchema.default('auto-cleanup'),
});
/**
 * 외부 sync 로그 — admin UI 의 /admin/wikis/external-sync-logs 에서 사용.
 * KMS 등 외부 시스템이 /api/wikis/external/sync 호출할 때마다 한 row 가 INSERT.
 */
export const WikiExternalSyncLogSchema = z.object({
    id: z.string().uuid(),
    externalId: z.string().nullable(),
    uploaderEmail: z.string().nullable(),
    /** KMS 에서 보낸 수정자 email. NULL = 수정 정보 미제공. */
    lastModifiedByEmail: z.string().nullable(),
    /** uploaderEmail lookup 으로 매칭된 user. NULL = admin fallback / 매칭 실패. */
    resolvedUser: z
        .object({
        id: z.string().uuid(),
        email: z.string(),
        displayName: z.string(),
    })
        .nullable(),
    /** lastModifiedByEmail lookup 으로 매칭된 user. NULL = 정보 없음 / 매칭 실패. */
    resolvedModifier: z
        .object({
        id: z.string().uuid(),
        email: z.string(),
        displayName: z.string(),
    })
        .nullable(),
    categorySlug: z.string().nullable(),
    kind: z.string(),
    title: z.string().nullable(),
    status: z.enum(['success', 'failed']),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    /** 성공 시 INSERT 된 wiki_documents.id + 표시명 + 현재 처리 상태. 실패면 NULL. */
    document: z
        .object({
        id: z.string().uuid(),
        originalName: z.string(),
        status: WikiDocumentStatusSchema.nullable(),
        digestSummary: WikiDigestSummarySchema.nullable(),
    })
        .nullable(),
    createdAt: z.string(),
});
export const WikiExternalSyncLogListSchema = z.object({
    logs: z.array(WikiExternalSyncLogSchema),
    /** 다음 페이지 cursor (id). null 이면 마지막. */
    nextCursor: z.string().nullable(),
    /** 검색·status 필터 적용 후 전체 카운트. UI 필터 chip 표시용. */
    total: z.number().int().optional(),
    successTotal: z.number().int().optional(),
    failedTotal: z.number().int().optional(),
});
// ─────────────────────────────────────────────────────────────────────────────
// 외부 그룹 (KMS opaque ACL) — 사용자/문서 sync 의 권한 정보
// ─────────────────────────────────────────────────────────────────────────────
/**
 * KMS 가 정한 외부 그룹 식별자. innermate 는 의미를 해석하지 않고 opaque text 로만 저장.
 * 정규화 안 함 — KMS 가 id 정합성 책임. trim 후 1~200자.
 */
export const ExternalGroupIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .refine((v) => !v.includes('\n') && !v.includes('\r'), 'external group id 에 줄바꿈 금지');
/**
 * KMS → innermate 사용자 sync. 한 사용자의 전체 그룹 목록을 full-replace.
 * 빈 배열 보내면 그 사용자의 모든 그룹 해제. 회수 시멘틱.
 *
 * 사용자는 innermate.users 에 미리 존재해야 한다 (email 매칭). 없으면 404.
 */
export const UserExternalSyncRequestSchema = z.object({
    /**
     * innermate.users.email 매칭 키 — 외부 시스템이 사번을 email 컬럼에 저장하는 케이스도
     * 있어 email 형식 검증은 하지 않음.
     */
    email: z.string().min(1).max(200),
    /** 외부 시스템의 표시명 — 변동 없을 때 보내지 않아도 됨. innermate 가 변경 안 함. */
    displayName: z.string().min(1).max(200).optional(),
    /** 이 사용자가 속한 외부 그룹 전체. 매 sync 마다 full-replace 된다. */
    groups: z.array(ExternalGroupIdSchema).max(500),
});
export const UserExternalSyncResponseSchema = z.object({
    userId: z.string().uuid(),
    email: z.string(),
    groupsCount: z.number().int().nonnegative(),
});
export const UserExternalDeleteResponseSchema = z.object({
    email: z.string(),
    removedGroups: z.number().int().nonnegative(),
});
export const WikiExternalDocumentDeleteResponseSchema = z.object({
    externalId: z.string(),
    removedDocuments: z.number().int().nonnegative(),
});
/**
 * 게시글의 ACL 만 갱신 — body 본문은 안 건드림. KMS 가 권한만 바꿨을 때 사용
 * (Permission/Scope CRUD hook). 영향받는 모든 wiki_documents (같은 externalId 의 stale rows
 * 포함) 의 ACL 매핑을 full-replace.
 */
export const WikiExternalAclUpdateRequestSchema = z.object({
    allowedGroups: z.array(ExternalGroupIdSchema).max(500),
});
export const WikiExternalAclUpdateResponseSchema = z.object({
    externalId: z.string(),
    updatedDocuments: z.number().int().nonnegative(),
    groupsCount: z.number().int().nonnegative(),
});
// ---------------------------------------------------------------------------
// 위키 점검(maintenance) — 깨진 백링크 / 깨진 원본 링크 스캔·정리
// ---------------------------------------------------------------------------
/** 본문에 끊긴 [[slug]] 가 있는 페이지 1행. */
export const BrokenBacklinkPageSchema = z.object({
    slug: z.string(),
    title: z.string(),
    deadSlugs: z.array(z.string()),
});
export const BrokenBacklinkScanSchema = z.object({
    pages: z.array(BrokenBacklinkPageSchema),
});
export const BrokenBacklinkCleanupSchema = z.object({
    pagesCleaned: z.number().int().nonnegative(),
    linksRemoved: z.number().int().nonnegative(),
});
/** 삭제된 출처 문서를 가리키는 페이지 1행. */
export const DeadSourceLinkPageSchema = z.object({
    slug: z.string(),
    title: z.string(),
    deadDocNames: z.array(z.string()),
});
export const DeadSourceLinkScanSchema = z.object({
    pages: z.array(DeadSourceLinkPageSchema),
});
export const DeadSourceLinkCleanupSchema = z.object({
    mappingsRemoved: z.number().int().nonnegative(),
    pagesAffected: z.number().int().nonnegative(),
    /** 유일 출처라 정리하면 고아가 되어 건너뛴 페이지 slug 들. */
    skipped: z.array(z.string()),
});
//# sourceMappingURL=wiki.js.map